import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  TextContainer,
  Badge,
  Button,
  DataTable,
  ProgressBar,
  Banner
} from '@shopify/polaris';
import { useAuth } from '../hooks/useAuth';
import { useStore } from '../contexts/StoreContext';
import { apiService } from '../services/api';

interface DashboardStats {
  totalJobs: number;
  completedJobs: number;
  pendingJobs: number;
  failedJobs: number;
  totalTranslations: number;
  successRate: number;
  averageProcessingTime: number;
}

interface LanguageStats {
  language: string;
  count: number;
  percentage: number;
}

interface ResourceTypeStats {
  resourceType: string;
  count: number;
  percentage: number;
}

interface RecentJob {
  id: number;
  name: string;
  status: string;
  targetLocale: string;
  resourceType: string;
  progress: number;
  createdAt: string;
}

export const Dashboard: React.FC = () => {
  const { user: _user } = useAuth();
  const { currentStore } = useStore();
  const [stats, setStats] = useState<DashboardStats>({
    totalJobs: 0,
    completedJobs: 0,
    pendingJobs: 0,
    failedJobs: 0,
    totalTranslations: 0,
    successRate: 0,
    averageProcessingTime: 0
  });
  const [languageStats, setLanguageStats] = useState<LanguageStats[]>([]);
  const [resourceTypeStats, setResourceTypeStats] = useState<ResourceTypeStats[]>([]);
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load translation jobs
      const jobsResponse = await apiService.getTranslationJobs();
      if (jobsResponse.data) {
        // Handle different response formats
        const responseData = jobsResponse.data as any;
        const jobs = Array.isArray(responseData) ? responseData : 
                    responseData.jobs ? responseData.jobs : [];
        
        // Calculate statistics
        const totalJobs = jobs.length;
        const completedJobs = jobs.filter((job: any) => job.status === 'completed').length;
        const pendingJobs = jobs.filter((job: any) => job.status === 'pending').length;
        const failedJobs = jobs.filter((job: any) => job.status === 'failed').length;
        const totalTranslations = jobs.reduce((sum: number, job: any) => sum + (job.translated_items || 0), 0);
        const successRate = totalJobs > 0 ? (completedJobs / totalJobs) * 100 : 0;
        
        setStats({
          totalJobs,
          completedJobs,
          pendingJobs,
          failedJobs,
          totalTranslations,
          successRate,
          averageProcessingTime: 0 // TODO: Calculate from job timestamps
        });

        // Calculate language statistics
        const languageCounts: { [key: string]: number } = {};
        jobs.forEach((job: any) => {
          const locale = job.target_locale;
          languageCounts[locale] = (languageCounts[locale] || 0) + 1;
        });

        const languageStatsArray = Object.entries(languageCounts)
          .map(([language, count]) => ({
            language: getLanguageLabel(language),
            count,
            percentage: (count / totalJobs) * 100
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        setLanguageStats(languageStatsArray);

        // Calculate resource type statistics
        const resourceTypeCounts: { [key: string]: number } = {};
        jobs.forEach((job: any) => {
          const resourceType = job.resource_type;
          resourceTypeCounts[resourceType] = (resourceTypeCounts[resourceType] || 0) + 1;
        });

        const resourceTypeStatsArray = Object.entries(resourceTypeCounts)
          .map(([resourceType, count]) => ({
            resourceType: getResourceTypeLabel(resourceType),
            count,
            percentage: (count / totalJobs) * 100
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        setResourceTypeStats(resourceTypeStatsArray);

        // Get recent jobs
        const recentJobsArray = jobs
          .slice(0, 5)
          .map((job: any) => ({
            id: job.id,
            name: job.settings?.name || `${getResourceTypeLabel(job.resource_type)} - ${getLanguageLabel(job.target_locale)}`,
            status: job.status,
            targetLocale: getLanguageLabel(job.target_locale),
            resourceType: getResourceTypeLabel(job.resource_type),
            progress: job.progress || 0,
            createdAt: new Date(job.created_at).toLocaleDateString()
          }));

        setRecentJobs(recentJobsArray);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  };

  const getLanguageLabel = (value: string) => {
    const languages = [
      { label: 'English', value: 'en' },
      { label: 'French', value: 'fr' },
      { label: 'German', value: 'de' },
      { label: 'Spanish', value: 'es' },
      { label: 'Italian', value: 'it' },
      { label: 'Portuguese', value: 'pt' },
      { label: 'Dutch', value: 'nl' },
      { label: 'Polish', value: 'pl' },
      { label: 'Swedish', value: 'sv' },
      { label: 'Finnish', value: 'fi' },
      { label: 'Danish', value: 'da' },
      { label: 'Norwegian', value: 'no' }
    ];
    const language = languages.find(lang => lang.value === value);
    return language ? language.label : value;
  };

  const getResourceTypeLabel = (value: string) => {
    const resourceTypes = [
      { label: 'Products', value: 'PRODUCT' },
      { label: 'Collections', value: 'COLLECTION' },
      { label: 'Pages', value: 'ONLINE_STORE_PAGE' },
      { label: 'Blog Posts', value: 'ONLINE_STORE_ARTICLE' },
      { label: 'Blogs', value: 'ONLINE_STORE_BLOG' },
      { label: 'Menus', value: 'ONLINE_STORE_MENU' },
      { label: 'Metafields', value: 'METAFIELD' },
      { label: 'Metaobjects', value: 'METAOBJECT' },
      { label: 'Product Options', value: 'PRODUCT_OPTION' },
      { label: 'Product Option Values', value: 'PRODUCT_OPTION_VALUE' },
      { label: 'Selling Plans', value: 'SELLING_PLAN' },
      { label: 'Selling Plan Groups', value: 'SELLING_PLAN_GROUP' },
      { label: 'Shop Policies', value: 'SHOP_POLICY' },
      { label: 'Payment Gateways', value: 'PAYMENT_GATEWAY' },
      { label: 'Delivery Methods', value: 'DELIVERY_METHOD_DEFINITION' },
      { label: 'Packing Slip Templates', value: 'PACKING_SLIP_TEMPLATE' },
      { label: 'Email Templates', value: 'EMAIL_TEMPLATE' },
      { label: 'Filters', value: 'FILTER' },
      { label: 'Links', value: 'LINK' },
      { label: 'Theme', value: 'ONLINE_STORE_THEME' }
    ];
    const resourceType = resourceTypes.find(res => res.value === value);
    return resourceType ? resourceType.label : value;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge tone="success">Completed</Badge>;
      case 'in_progress':
        return <Badge tone="info">In Progress</Badge>;
      case 'pending':
        return <Badge tone="warning">Pending</Badge>;
      case 'failed':
        return <Badge tone="critical">Failed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const recentJobsRows = recentJobs.map(job => [
    <Text variant="bodyMd" fontWeight="semibold" as="span" key={job.id}>
      {job.name}
    </Text>,
    getStatusBadge(job.status),
    <Text variant="bodyMd" as="span">{job.targetLocale}</Text>,
    <Text variant="bodyMd" as="span">{job.resourceType}</Text>,
    <div key={job.id}>
      <ProgressBar progress={job.progress} size="small" />
      <Text variant="bodySm" as="p">{job.progress}%</Text>
    </div>,
    <Text variant="bodyMd" as="span">{job.createdAt}</Text>
  ]);

  return (
    <Page
      title="Dashboard"
      primaryAction={{
        content: 'Start New Translation',
        onAction: () => window.location.href = '/translations'
      }}
    >
      <Layout>
        <Layout.Section>
          <Banner title="Welcome to TranslateFlow" tone="success">
            <p>AI-Powered Shopify Translation Made Simple. Monitor your translation progress and manage your multilingual content.</p>
          </Banner>
        </Layout.Section>

        {/* Store Information */}
        {currentStore && (
          <Layout.Section>
            <Card>
              <div style={{ padding: '20px' }}>
                <TextContainer>
                  <Text variant="headingMd" as="h2">
                    Connected Store
                  </Text>
                  <div style={{ marginTop: '12px' }}>
                    <Text variant="bodyMd" as="p">
                      <strong>Domain:</strong> {currentStore.shopifyDomain}
                    </Text>
                    {currentStore.shopifyShopName && (
                      <Text variant="bodyMd" as="p">
                        <strong>Shop Name:</strong> {currentStore.shopifyShopName}
                      </Text>
                    )}
                    <Text variant="bodyMd" as="p">
                      <strong>Status:</strong> {currentStore.isActive ? 'Active' : 'Inactive'}
                    </Text>
                    {currentStore.createdAt && (
                      <Text variant="bodyMd" as="p">
                        <strong>Connected:</strong> {new Date(currentStore.createdAt).toLocaleDateString()}
                      </Text>
                    )}
                  </div>
                </TextContainer>
              </div>
            </Card>
          </Layout.Section>
        )}

        {/* Statistics Cards */}
        <Layout.Section>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            <Card>
              <div style={{ padding: '20px' }}>
                <TextContainer>
                  <Text variant="headingMd" as="h2">
                    Total Jobs
                  </Text>
                  <Text variant="headingLg" as="p" fontWeight="bold">
                    {stats.totalJobs}
                  </Text>
                  <Text variant="bodySm" as="p" tone="subdued">
                    All time translation jobs
                  </Text>
                </TextContainer>
              </div>
            </Card>

            <Card>
              <div style={{ padding: '20px' }}>
                <TextContainer>
                  <Text variant="headingMd" as="h2">
                    Completed
                  </Text>
                  <Text variant="headingLg" as="p" fontWeight="bold" tone="success">
                    {stats.completedJobs}
                  </Text>
                  <Text variant="bodySm" as="p" tone="subdued">
                    Successfully completed
                  </Text>
                </TextContainer>
              </div>
            </Card>

            <Card>
              <div style={{ padding: '20px' }}>
                <TextContainer>
                  <Text variant="headingMd" as="h2">
                    Total Translations
                  </Text>
                  <Text variant="headingLg" as="p" fontWeight="bold">
                    {stats.totalTranslations.toLocaleString()}
                  </Text>
                  <Text variant="bodySm" as="p" tone="subdued">
                    Individual items translated
                  </Text>
                </TextContainer>
              </div>
            </Card>

            <Card>
              <div style={{ padding: '20px' }}>
                <TextContainer>
                  <Text variant="headingMd" as="h2">
                    Success Rate
                  </Text>
                  <Text variant="headingLg" as="p" fontWeight="bold" tone="success">
                    {stats.successRate.toFixed(1)}%
                  </Text>
                  <Text variant="bodySm" as="p" tone="subdued">
                    Job completion rate
                  </Text>
                </TextContainer>
              </div>
            </Card>
          </div>
        </Layout.Section>

        {/* Language and Resource Type Analytics */}
        <Layout.Section>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '16px' }}>
            <Card>
              <div style={{ padding: '20px' }}>
                <TextContainer>
                  <Text variant="headingMd" as="h2">
                    Top Languages
                  </Text>
                  <Text variant="bodyMd" as="p">
                    Most translated languages
                  </Text>
                </TextContainer>

                {languageStats.length > 0 ? (
                  <div style={{ marginTop: '16px' }}>
                    {languageStats.map((stat, index) => (
                      <div
                        key={stat.language}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 0',
                          borderBottom: index < languageStats.length - 1 ? '1px solid #e1e3e5' : 'none'
                        }}
                      >
                        <div>
                          <Text variant="bodyMd" as="p" fontWeight="semibold">
                            {stat.language}
                          </Text>
                          <Text variant="bodySm" as="p" tone="subdued">
                            {stat.count} jobs
                          </Text>
                        </div>
                        <div style={{ width: '100px' }}>
                          <ProgressBar progress={stat.percentage} size="small" />
                          <Text variant="bodySm" as="p" tone="subdued">
                            {stat.percentage.toFixed(1)}%
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text variant="bodyMd" as="p">No language data available</Text>
                  </div>
                )}
              </div>
            </Card>

            <Card>
              <div style={{ padding: '20px' }}>
                <TextContainer>
                  <Text variant="headingMd" as="h2">
                    Top Resource Types
                  </Text>
                  <Text variant="bodyMd" as="p">
                    Most translated content types
                  </Text>
                </TextContainer>

                {resourceTypeStats.length > 0 ? (
                  <div style={{ marginTop: '16px' }}>
                    {resourceTypeStats.map((stat, index) => (
                      <div
                        key={stat.resourceType}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 0',
                          borderBottom: index < resourceTypeStats.length - 1 ? '1px solid #e1e3e5' : 'none'
                        }}
                      >
                        <div>
                          <Text variant="bodyMd" as="p" fontWeight="semibold">
                            {stat.resourceType}
                          </Text>
                          <Text variant="bodySm" as="p" tone="subdued">
                            {stat.count} jobs
                          </Text>
                        </div>
                        <div style={{ width: '100px' }}>
                          <ProgressBar progress={stat.percentage} size="small" />
                          <Text variant="bodySm" as="p" tone="subdued">
                            {stat.percentage.toFixed(1)}%
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text variant="bodyMd" as="p">No resource type data available</Text>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </Layout.Section>

        {/* Recent Activity */}
        <Layout.Section>
          <Card>
            <div style={{ padding: '20px' }}>
              <TextContainer>
                <Text variant="headingMd" as="h2">
                  Recent Activity
                </Text>
                <Text variant="bodyMd" as="p">
                  Latest translation jobs and their progress
                </Text>
              </TextContainer>

              {recentJobs.length > 0 ? (
                <div style={{ marginTop: '16px' }}>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                    headings={[
                      'Job Name',
                      'Status',
                      'Language',
                      'Resource Type',
                      'Progress',
                      'Created'
                    ]}
                    rows={recentJobsRows}
                  />
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Text variant="bodyMd" as="p">No recent jobs found</Text>
                  <div style={{ marginTop: '12px' }}>
                    <Button variant="primary" onClick={() => window.location.href = '/translations'}>
                      Start Your First Translation
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </Layout.Section>

        {/* Quick Actions */}
        <Layout.Section>
          <Card>
            <div style={{ padding: '20px' }}>
              <TextContainer>
                <Text variant="headingMd" as="h2">
                  Quick Actions
                </Text>
                <Text variant="bodyMd" as="p">
                  Common tasks and shortcuts
                </Text>
              </TextContainer>

              <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                <Button
                  variant="primary"
                  onClick={() => window.location.href = '/translations'}
                  fullWidth
                >
                  Start New Translation
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => window.location.href = '/translations'}
                  fullWidth
                >
                  View All Jobs
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => window.location.href = '/settings'}
                  fullWidth
                >
                  Manage Settings
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => window.location.href = '/resources'}
                  fullWidth
                >
                  Resource Management
                </Button>
              </div>
            </div>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}; 